// Feature 087 (ticket 15) — approved treatment from ticket 10's prototype,
// promoted to production. Placed directly below the Care Stage section.
import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Gem, MapPin, Star } from 'lucide-react';
import { avatarColorClass, getInitials } from './avatar';
import { DeliveryModeChips } from './DeliveryModeChips';
import type { BestMatchEntry } from './bestMatch';

type BestMatchHighlightProps = {
  entry: BestMatchEntry;
  /** Surface-specific buttons (Message / Request consultation …) for the match. */
  actions?: ReactNode;
};

export const BestMatchHighlight = ({ entry, actions }: BestMatchHighlightProps) => {
  const { provider, category } = entry;
  const rating =
    typeof provider.rating === 'number' && provider.rating > 0 ? provider.rating.toFixed(1) : null;
  const bio = provider.bio?.trim();

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[hsl(38_55%_68%)]/50 bg-gradient-to-r from-[hsl(43_70%_96%)] to-[hsl(38_65%_92%)] px-5 py-4 shadow-[0_3px_14px_rgba(160,120,30,0.15)]">
      <motion.div
        className="pointer-events-none absolute inset-y-0 -left-1/4 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent"
        animate={{ x: ['0%', '520%'] }}
        transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 3.4, ease: 'easeInOut' }}
        aria-hidden
      />

      <div className="relative flex items-start gap-4">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-[hsl(38_55%_65%)]/60 bg-gradient-to-br from-[hsl(43_78%_92%)] to-[hsl(30_70%_80%)] text-[hsl(30_65%_26%)] shadow-[0_1px_4px_rgba(160,120,30,0.25)]">
          <Gem className="h-4.5 w-4.5" aria-hidden />
        </span>

        <div className="min-w-0 flex-1 overflow-hidden">
          <p
            title="Highest-scoring provider across this care plan. Primary specialty breaks score ties."
            aria-label="Best Match: Highest-scoring provider across this care plan. Primary specialty breaks score ties."
            className="text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(32_55%_38%)]"
          >
            Best Match
          </p>

          <div className="mt-0.5 flex items-center gap-2.5">
            <span
              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${avatarColorClass(provider.name)}`}
              aria-hidden
            >
              {getInitials(provider.name)}
            </span>
            <div className="min-w-0">
              <a
                href={`/providers/${provider.id}?discoverySource=NAVIGATOR`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-termina text-sm font-medium text-text-primary hover:text-sage hover:underline"
              >
                {provider.name}
              </a>
              <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-secondary">
                <span>{category.name}</span>
                {provider.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" aria-hidden />
                    {provider.location}
                  </span>
                )}
                {rating && (
                  <span className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-sage text-sage" aria-hidden />
                    {rating}
                  </span>
                )}
                <DeliveryModeChips modes={provider.specialtyDeliveryModes} compact />
              </p>
            </div>
          </div>

          {/*
            Bio and actions are one treatment, added by 105 for the care-seeker
            guide: the bio is what makes the actions worth taking. The advisor
            and shared-plan views pass no actions and were never part of that
            change, so they keep the compact card they already had.
          */}
          {actions && (
            <>
              {bio && <p className="mt-2 line-clamp-2 text-sm text-text-secondary">{bio}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">{actions}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
